import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  PLATFORM_ID,
  provideAppInitializer,
  TransferState
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi
} from '@angular/common/http';
import {
  provideRouter,
  withDisabledInitialNavigation,
  withInMemoryScrolling,
  withComponentInputBinding,
  withRouterConfig,
  UrlSerializer
} from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  LocalizeParser,
  LocalizeRouterService,
  LocalizeRouterSettings,
  withLocalizeRouter
} from '@gilsdav/ngx-translate-router';
import { routes } from './app.routes';
import {
  PlatformassetPipe,
  PlatformInformationService,
  PlatformInformationServiceConfigurator,
  PlatformurlPipe
} from '../../projects/platform-manager/src/public_api';
import { LocalizeUniversalLoader } from './shared/classes/localize-universal-loader';
import { RequestCache } from './core/services/request-cache.service';
import { TranslateHttpLoader } from './ssr/TranslateHttpLoader';
import { DatePipe, Location } from '@angular/common';
import { environment } from '../environments/environment';
import { LocaleLoaderService } from './core/services/locale-loader.service';
import { Myserializer } from './shared/services/myserializer';
import { BASE_PATH as BOOKING_BASE_PATH } from '@frontend';
import { BASE_PATH } from './pages/product-selection/generated';
import { abTestConfig, AbTestService, CONFIG } from './shared/abtest';
import {
  AbTestInterceptor,
  CachingInterceptor,
  SimpleBookingInterceptor,
  TokenInterceptorService
} from './core/interceptors';
import {
  provideClientHydration,
  withHttpTransferCacheOptions,
  withIncrementalHydration
} from '@angular/platform-browser';
import { SentryService } from './core/services/sentry.service';
import { CustomErrorHandler } from './shared/services/custom-error-handler.service';

export const appInitializerFactory = (injector: Injector) => {
  return () => {
    const localize = injector.get(LocalizeRouterService);
    return localize.hooks.initialized.toPromise();
  };
};

export function initializeAppLocale(localeService: LocaleLoaderService): () => Promise<void> {
  return () => localeService.loadInitialLocaleData();
}

export const config = {
  production: environment.production,
  env: environment.env,
  hostname: environment.hostname,
  payment_hostname: environment.payment_hostname,
  language: environment.language,
  api_endpoints: environment.api.url,
  platform: environment.platform,
  platformFallback: environment.platformFallback,
  sales: environment.sales
};

export class TranslateConfig {
  path_server: string;
  path_client: string;
}

export function createTranslateLoader(transferstate: TransferState, platformId: object) {
  return new TranslateHttpLoader(transferstate, platformId);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(
      withIncrementalHydration(),
      withHttpTransferCacheOptions({ includePostRequests: false })
    ),
    // make awaited api call to fully setup the platformservice
    provideAppInitializer(() => {
      const platform = inject(PlatformInformationService);
      return platform.init();
    }),
    // provideZoneChangeDetection({ eventCoalescing: true }), //THIS IS THE ONE!
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideRouter(
      routes,
      withLocalizeRouter(routes, {
        initialNavigation: true,
        parser: {
          provide: LocalizeParser,
          useFactory: (
            translate: TranslateService,
            location: Location,
            settings: LocalizeRouterSettings,
            platformservice: PlatformInformationService
          ) =>
            new LocalizeUniversalLoader(
              translate,
              location,
              settings,
              platformservice,
              platformservice.getAvailableLanguagesForPlatform()
            ),
          deps: [TranslateService, Location, LocalizeRouterSettings, PlatformInformationService]
        },
        defaultLangFunction: (languages: Array<string>, cachedLang?: string, browserLang?: string) => {
          if (cachedLang) {
            return cachedLang;
          }
          if (browserLang) {
            return browserLang;
          }
          return languages[0];
        }
      }),
      withDisabledInitialNavigation(),
      withInMemoryScrolling({ anchorScrolling: 'enabled' }),
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      }),
      withComponentInputBinding()
    ),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [TransferState, PLATFORM_ID]
        },
        useDefaultLang: false
      })
    ]),
    PlatformassetPipe,
    RequestCache,
    { provide: UrlSerializer, useClass: Myserializer },
    { provide: HTTP_INTERCEPTORS, useClass: CachingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AbTestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SimpleBookingInterceptor, multi: true },
    { provide: ErrorHandler, useClass: CustomErrorHandler, deps: [SentryService] },
    {
      provide: TranslateConfig,
      useValue: { path_server: environment.language_path_server, path_client: environment.language_path }
    },
    {
      provide: BOOKING_BASE_PATH,
      useFactory: (platformService: PlatformInformationService) => {
        // when we want the product/cart apis to point somewhere else, change me
        return platformService.configuration.payment_hostname;
      },
      deps: [PlatformInformationService]
    },
    {
      provide: BASE_PATH,
      useFactory: (platformService: PlatformInformationService) => {
        // when we want the product/cart apis to point somewhere else, change me
        return platformService.configuration.hostname;
      },
      deps: [PlatformInformationService]
    },
    AbTestService,
    { provide: CONFIG, useValue: abTestConfig },
    {
      // TODO: this needs to be an async provider that fetches the new api-config endpoint
      provide: PlatformInformationServiceConfigurator,
      useValue: config
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [Injector],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppLocale,
      deps: [LocaleLoaderService],
      multi: true
    },
    DatePipe,
    PlatformurlPipe
  ]
};
